import React, { useState, useEffect } from 'react';
import { Link, Switch, Route, useLocation, useRouteMatch, useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import AboutPayer from './about-payer';
import { BASE_URL, DRAWER_BREAK_POINT } from '../../utils';
import { gray, blue, salmon, theme } from '../../theme';

const drawerWidth = window.innerWidth <= DRAWER_BREAK_POINT ? window.innerWidth : 320;
const NAVBAR_HEIGHT = 73;

const payerIconSmall = (
  <svg
    width="24"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 4a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Zm12.073-2L3.502 4.857v8.571A8.57 8.57 0 0 0 12.073 22a8.57 8.57 0 0 0 8.571-8.572V4.857L12.073 2Zm0 18.393a6.965 6.965 0 0 0 6.965-6.965V6.063l-6.964-2.455-6.965 2.455v7.366a6.965 6.965 0 0 0 6.965 6.965Z"
      fill="#00A0FF"
    />
  </svg>
);

const PayerDashboard = (props) => {
  const [isMobileLayout, setMobileLayout] = useState(false);
  const [isDrawerOpen, toggleDrawer] = useState(true);
  const [activePayer, setActivePayer] = useState({});

  const location = useLocation();
  const history = useHistory();
  const { pathname } = location;
  const { path, url } = useRouteMatch();
  const { payerId } = useParams();

  // Split panel theme
  const {
    drawerBorderColor,
    // Drawer Tabs
    tabBackground,
    tabActiveBackground,
    tabHoverBackground,
    tabIndicatorBackground,
    tabIndicatorHoverBackground,
    tabIndicatorActiveBackground,
    tabTextColor,
    tabHoverTextColor,
    tabActiveTextColor,
    headerBackground,
    headerTextColor,
    groupTabIndent
    // drawerBackground,
    // drawerTextColor,
    // drawerFontSize,
    // drawerFontWeight,
    // tabFontSize,
    // tabFontWeight,
    // groupSeparators,
    // headerFontSize,
    // headerFontWeight,
    // groupHeaderTextColor,
    // groupHeaderTextOpacity,
    // groupHeaderFontSize,
    // groupHeaderFontWeight
  } = theme.splitPanelDrawer;

  const loadPayerData = () => {
    const url = new URL(`${BASE_URL}/payers2/getPayerProfile`);
    const params = { id: payerId };
    url.search = new URLSearchParams(params).toString();

    fetch(url)
      .then(response => response.text())
      .then(result => {
        const reqData = JSON.parse(result);
        setActivePayer(reqData.payer);
      })
      .catch(error => console.log('error', error));
  };

  const handleWindowResize = () => {
    const isMobile = window.innerWidth <= 768;
    toggleDrawer(!isMobile);
    setMobileLayout(isMobile);
  };

  // Effects hook
  useEffect(() => {
    loadPayerData();
  }, []);

  // Window resize event effect
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // ------------------------------ Styles ------------------------------
  const styles = StyleSheet.create({
    dashboardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '32px',
      padding: '0 8px',
      background: gray[500],
      color: gray[300]
    },
    dashboardHeaderIcon: {
      fontSize: '18px',
      color: salmon[500],
      cursor: 'pointer'
    },
    splitPanel: {
      display: 'flex',
      flexFlow: 'row nowrap',
      background: gray[800],
      height: 'calc(100% - 32px)',
      width: '100%',
      overflow: 'hidden',
      fontFamily: 'Lato'
    },
    drawer: {
      position: 'relative',
      height: '100%',
      transition: '0.3s',
      padding: '0',
      // overflowY: 'auto',
      zIndex: '1900',
      borderRight: `1px solid ${gray[700]}`,
      background: gray[800]
    },
    openDrawer: {
      left: '0',
      minWidth: `${drawerWidth}px`,
      width: `${drawerWidth}px`
    },
    hideDrawer: {
      transition: '0.3s',
      width: '0px',
      minWidth: '0px',
      left: `calc(-1 * ${drawerWidth}px)`
    },
    drawerHeader: {
      height: '40px',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'left',
      cursor: 'pointer',
      background: headerBackground,
      color: headerTextColor,
      borderBottom: `0.5px solid ${drawerBorderColor}`,
      ':hover:not(.activeitem)': {
        background: tabHoverBackground,
        color: tabHoverTextColor
      }
    },
    drawerHeaderActive: {
      color: tabActiveTextColor,
      background: tabActiveBackground
    },
    drawerHeaderIconContainer: {
      height: '32px',
      width: '32px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 4px',
      cursor: 'pointer'
    },
    drawerHeaderIcon: {
      fontSize: '24px',
      cursor: 'pointer'
    },
    drawerHeaderTextContainer: {
      textAlign: 'left',
      lineHeight: '16px',
      cursor: 'pointer'
    },
    drawerHeaderText: {
      fontSize: '14px',
      cursor: 'pointer',
      paddingLeft: '4px',
      fontWeight: 'bold'
    },
    drawerHeaderTextSub: {
      fontSize: '12px',
      cursor: 'pointer',
      paddingLeft: '4px'
    },
    drawerTabsContainer: {
      // height: `calc(100% - ${NAVBAR_HEIGHT + 64}px)`,
      height: 'calc(100% - 40px)',
      overflowY: 'auto'
    },
    drawerAccordian: {
      position: 'relative',
      display: 'block',
      transition: '0.5s ease',
      maxHeight: '2000px'
    },
    drawerFile: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '4px',
      color: blue[500],
      height: '40px'
    },
    drawerMenuItem: {
      position: 'relative',
      background: tabBackground,
      color: tabTextColor,
      fill: tabTextColor,
      borderLeft: `1px solid ${tabIndicatorBackground}`,
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'left',
      paddingLeft: groupTabIndent,
      ':hover:not(.activeitem)': {
        background: tabHoverBackground,
        color: tabHoverTextColor,
        fill: tabHoverTextColor,
        borderLeft: `1px solid ${tabIndicatorHoverBackground}`
      }
    },
    drawerMenuItemIcon: {
      height: '24px',
      width: '30px',
      minWidth: '24px',
      marginRight: '8px',
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px'
    },
    drawerMenuItemActive: {
      borderLeft: `1px solid ${tabIndicatorActiveBackground}`,
      color: tabActiveTextColor,
      background: tabActiveBackground
    },
    drawerMenuItemText: {
      width: '100%',
      fontSize: '1rem',
      lineHeight: '14px'
    },
    linkStyles: {
      display: 'flex',
      alignItems: 'center',
      color: gray[300]
    },
    selectedLink: {
      color: blue[500]
    },
    menuItem: {
      position: 'relative',
      left: '-3px',
      color: gray[300]
    },
    detail: {
      // position: 'relative',
      transition: '0s',
      height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      width: 'calc(100vw - 320px)',
      left: '0'
    },
    detailExapnd: {
      transition: '0.2s',
      left: 'calc(-1 * 320px)',
      width: '100vw'
    },
    detailHeader: {
      height: '40px',
      // background: '#001824',
      background: gray[800],
      display: 'flex',
      alignItems: 'center',
      borderBottom: '0.5px solid #082840',
      padding: '0 8px'
    }
  });

  const handleOnClickMiniUserCard = () => {
    history.push(`${url}/profile`);
    if (isMobileLayout) {
      toggleDrawer(false);
    }
  };

  const handleReloadPayerData = () => {
    loadPayerData();
  };

  const activeText = 'Payer Profile';

  return (
    <div style={{ height: 'calc(100% - 40px)' }}>
      {/* Dashboard Header */}
      <div className={css(styles.dashboardHeader)}>
        <div>Payer Profile</div>
        <div
          className={css(styles.dashboardHeaderIcon)}
          onClick={() => {
            history.push('/admin/payers');
          }}
        >
          <i className='fa fa-times-circle' />
        </div>
      </div>
      <div className={css(styles.splitPanel)}>
        {/* Panel Drawer */}
        <div
          className={css(styles.drawer, isDrawerOpen ? styles.openDrawer : styles.hideDrawer)}
        >
          {/* Drawer header (Mini user card) */}
          <div
            // className={css(styles.drawerHeader)}
            className={`
            ${(pathname.endsWith('/profile') || pathname.endsWith('/profile/about') || pathname.endsWith('/profile/contact')) ? 'activeitem' : ''}
            ${css(
              styles.drawerHeader,
              (pathname.endsWith('/profile') || pathname.endsWith('/profile/about') || pathname.endsWith('/profile/contact')) && styles.drawerHeaderActive
            )}`}
            onClick={handleOnClickMiniUserCard}
          >
            {/* Header icon */}
            <div className={css(styles.drawerHeaderIconContainer)}>
                {/* <i className={`fa fa-hospital-o ${css(styles.drawerHeaderIcon)}`} /> */}
                {payerIconSmall}
            </div>
            {/* Header text */}
            <div className={css(styles.drawerHeaderTextContainer)}>
              <div className={`line-clamp_1 ${css(styles.drawerHeaderText)}`}>
                {activePayer.payerName}
              </div>
              <div className={css(styles.drawerHeaderTextSub)}>
                {`@${activePayer.payerId}`}
              </div>
            </div>
          </div>
          {/* Drawer tabs */}
          <div className={css(styles.drawerTabsContainer)} />
        </div>
        {/* Panel details */}
        <div className={css(styles.detail, !isDrawerOpen && styles.detailExapnd)}>
          <div className={css(styles.detailHeader)}>
            {/* <div>{activeText}</div> */}
            <div>{`${activePayer.payerName}'s Profile`}</div>
          </div>
          {/* Active component */}
          <Switch>
            <Route exact path={`${path}`}>
              <AboutPayer
                data={activePayer}
                reloadData={handleReloadPayerData}
              />
            </Route>
            <Route path={`${path}/profile`}>
              <AboutPayer
                data={activePayer}
                reloadData={handleReloadPayerData}
              />
            </Route>
          </Switch>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps({ user }) {
  return { ...user };
}

export default connect(mapStateToProps, {})(PayerDashboard);
